/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { Brand } from '@/lib/brand/brand';

/**
 * Curated per-brand search sort options.
 *
 * WHERE THE LISTS COME FROM. Both brands' raw server-rendered SRP dumps embed
 * the page's own hydration state, and that state carries the sort list live
 * actually offers, verbatim, as `"sortOptions":[{<id>:<label>,isDefault}]`:
 *
 *   .reference/search-dress.desktop.html      (live NM, 11,106 results)
 *     final_price desc          Price: high to low
 *     final_price asc           Price: low to high
 *     launch_date desc          Newest First
 *     best_seller_attribute desc  Best Sellers
 *     BEST_MATCH                Best Match          isDefault: true
 *
 *   .reference/bg-search-dress.desktop.html   (live BG, 3,726 results)
 *     final_price desc          Price: high to low
 *     final_price asc           Price: low to high
 *     launch_date desc          Newest First
 *     BEST_MATCH                Best Match          isDefault: true
 *
 * THE BRANDS DIFFER BY ONE ROW, AND IT IS A MEASURED DIFFERENCE, not an
 * inference: live BG has NO "Best Sellers" option. Handing BG the NM list
 * would put a fifth control on its menu that its own page does not offer.
 *
 * ORDER is the array order above, and that is a reading rather than a guess:
 * live NM renders a native <select>, so its dump also contains the rendered
 * `<option>` elements — and they appear in exactly the array order, with
 * `BEST_MATCH` carrying `selected` in LAST place. So live's menu does not
 * float the default to the top, and BG's list is ordered the same way.
 * NM's curated list below now carries that same live order — Best Match LAST,
 * marked `isDefault: true`. It previously sat Best-Match-first, and the
 * divergence was tracked rather than silently fixed; this change closes it.
 *
 * Consumers must resolve the no-`?sort=` selection with `defaultSortId()`, NEVER
 * `options[0]`. Those two agreed only while Best Match happened to sit first, so
 * position-0 reads silently began displaying `price-high-to-low` on a page the
 * loader had not sorted that way.
 *
 * IDS ARE NOT LIVE'S IDS. `final_price desc` / `BEST_MATCH` are the legacy
 * search-engine's own sort keys; SCAPI takes a storefront sorting-option id.
 * The ids below are the SFCC-conventional ids those labels map to, the same
 * mapping NM has used since the sort row landed.
 *
 * SANDBOX STATE — the gap this file used to describe is CLOSED. bkcb-004 now
 * HAS storefront sorting options configured in BM, and productSearch returns
 * them, so {@link resolveSortOptions} takes its server-list branch on every
 * deployed page. Verified 2026-08-22 against the deployed sandbox: both brands
 * render the server labels in the server's order, and `?sort=price-low-to-high`
 * returns an ascending grid (NM 197.50 / 370 / 395 / 398) while
 * `?sort=price-high-to-low` returns a descending one (3,200 / 1,990 / 1,370 /
 * 1,060). The previous text here — "the sandbox has NO storefront sorting
 * options configured … the data is not there yet" — is now false; it is
 * rewritten rather than left because the first-entry fallback below was written
 * FOR that unreachable case and became live the day the data landed.
 *
 * BM'S ORDER IS NOT LIVE'S ORDER. BM returns `price-low-to-high` first; live
 * puts `price-high-to-low` first (see the dump transcription above). The server
 * list wins for RENDER ORDER, so the deployed menu's first two entries are
 * swapped relative to live. That is a separate, display-only divergence from
 * the selection defect this module fixes — it is a BM data-ordering question,
 * not a code one, and reordering in BM would ALSO be the wrong fix for
 * selection, because live's own default sits LAST.
 */
/** i18n keys of the curated options — literal union so typed `t()` accepts them. */
export type SortLabelKey =
    | 'sort.bestMatch'
    | 'sort.priceHighToLow'
    | 'sort.priceLowToHigh'
    | 'sort.newestFirst'
    | 'sort.bestSellers';

/** Pre-existing name, kept so NM's call sites read unchanged. */
export type NmSortLabelKey = SortLabelKey;

export interface SortOption {
    /** SCAPI sorting-option id, sent as productSearch's `sort` parameter. */
    id: string;
    /** i18n key inside the `search` namespace. */
    labelKey: SortLabelKey;
    /** Live verbatim label, used as the i18n defaultValue. */
    defaultLabel: string;
    /**
     * Live's own `isDefault` flag. BOTH brands need it: each brand's list ends
     * on its default rather than starting on it, so "first entry" is never the
     * selected one when the URL carries no `?sort=`. It is also the flag
     * {@link resolveSortOptions} carries ACROSS onto a server-provided list,
     * which has no default of its own — see that function.
     */
    isDefault?: boolean;
}

/** Pre-existing name, kept so NM's call sites read unchanged. */
export type NmSortOption = SortOption;

export const NM_SORT_OPTIONS: readonly SortOption[] = [
    { id: 'price-high-to-low', labelKey: 'sort.priceHighToLow', defaultLabel: 'Price: high to low' },
    { id: 'price-low-to-high', labelKey: 'sort.priceLowToHigh', defaultLabel: 'Price: low to high' },
    { id: 'newest', labelKey: 'sort.newestFirst', defaultLabel: 'Newest First' },
    { id: 'top-sellers', labelKey: 'sort.bestSellers', defaultLabel: 'Best Sellers' },
    { id: 'best-matches', labelKey: 'sort.bestMatch', defaultLabel: 'Best Match', isDefault: true },
];

/**
 * BG's four, in live BG's own array order, with live's default marked.
 * No "Best Sellers" row — see the dump comparison above.
 */
export const BG_SORT_OPTIONS: readonly SortOption[] = [
    { id: 'price-high-to-low', labelKey: 'sort.priceHighToLow', defaultLabel: 'Price: high to low' },
    { id: 'price-low-to-high', labelKey: 'sort.priceLowToHigh', defaultLabel: 'Price: low to high' },
    { id: 'newest', labelKey: 'sort.newestFirst', defaultLabel: 'Newest First' },
    { id: 'best-matches', labelKey: 'sort.bestMatch', defaultLabel: 'Best Match', isDefault: true },
];

/**
 * Brand-keyed curated fallbacks (pattern: SEARCH_UX_BY_BRAND — keyed maps,
 * never inline branches). Each brand's list is read off that brand's own dump;
 * neither is the other's default.
 *
 * THE DUMP EACH ARM IS READ OFF, named rather than described:
 *   nm -> .reference/search-dress.desktop.html:1223
 *   bg -> .reference/bg-search-dress.desktop.html:110
 * Both lines carry the page's own hydration state, whose `"sortOptions":[…]`
 * array is live's verbatim list. RE-DERIVED 2026-08-19 with `/usr/bin/grep -a`
 * and a `div` positive control in the same pass: NM's array is the five rows
 * transcribed in the file header, in that order, with `"BEST_MATCH":"Best
 * Match","isDefault":"true"` LAST — and NM's rendered `<option selected>` is
 * that same last entry, so live really does not float its default to the top.
 * BG's array is the same four minus `best_seller_attribute desc`. The raw dumps
 * emit the page ONCE (unlike the `captures/` tier), and `sortOptions":[{` occurs
 * on exactly one line in each, so no halving applies to these two readings.
 */
export const SORT_OPTIONS_BY_BRAND: Record<Brand, readonly SortOption[]> = {
    nm: NM_SORT_OPTIONS,
    bg: BG_SORT_OPTIONS,
};

/** A resolved option: either a server-provided {id,label} or a curated fallback entry. */
export interface ResolvedSortOption {
    id: string;
    /** Present on server-provided options — render as-is. */
    label?: string;
    /** Present on curated fallback options — translate via the search namespace. */
    labelKey?: SortLabelKey;
    defaultLabel?: string;
    /**
     * Carried through from the curated entry — on a curated list directly, and
     * on a server-provided list by id match against the curated one. Absent
     * when the server offers an id the curated list does not mark default.
     */
    isDefault?: boolean;
}

/**
 * Server-provided sorting options win (real BM configuration beats a curated
 * list); the brand's curated list covers the current unconfigured sandbox.
 *
 * `fallback` IS REQUIRED ON PURPOSE. It used to default to NM's list, which is
 * the shape this codebase has been unpicking everywhere else: NM silently
 * standing in as the neutral. A caller now has to name the brand list it means,
 * so a new surface cannot inherit NM's answer by omission.
 */
export function resolveSortOptions(
    serverOptions: Array<{ id?: string; label?: string }> | undefined | null,
    fallback: readonly SortOption[]
): ResolvedSortOption[] {
    const server = (serverOptions ?? []).filter((o): o is { id: string; label?: string } => Boolean(o.id));
    if (server.length > 0) {
        // WHY THE FLAG IS CARRIED ACROSS. SCAPI's `sortingOptions` carry an id
        // and a label and nothing else — no marker for which one the search
        // runs when no `sort` parameter is sent. So a server list on its own
        // cannot answer "what is selected with no `?sort=`", and
        // {@link defaultSortId} used to answer it with `options[0]`.
        //
        // That was harmless only while this branch was unreachable. With BM
        // configured, BM returns `price-low-to-high` FIRST, so position 0 made
        // both brands paint `<option value="price-low-to-high" selected>` over
        // a grid in default order — measured on the deployed sandbox
        // 2026-08-22, NM at 440 / 435 / 395 / 480, which is plainly not
        // ascending, and byte-for-byte the same grid `?sort=best-matches`
        // returns. The control named a sort the results were not in.
        //
        // The curated list is the only place the default is recorded, because
        // it is read off live's own `isDefault`. Matching on id carries that
        // one fact across without touching the server's labels or its order —
        // both of which are real BM configuration and should win.
        //
        // An id BM offers that the curated list does not mark stays unmarked,
        // so a list sharing no ids with the curated one keeps the pre-existing
        // first-entry behaviour rather than silently acquiring a default.
        const defaultIds = new Set(fallback.filter((o) => o.isDefault).map((o) => o.id));
        return server.map((o) => ({
            id: o.id,
            label: o.label ?? o.id,
            // Spread so the property is ABSENT rather than `undefined` on the
            // rows that are not the default — `find((o) => o.isDefault)` reads
            // the same either way, but an absent key keeps the resolved shape
            // honest about which rows carry the flag.
            ...(defaultIds.has(o.id) ? { isDefault: true } : {}),
        }));
    }
    return fallback.map((o) => ({
        id: o.id,
        labelKey: o.labelKey,
        defaultLabel: o.defaultLabel,
        isDefault: o.isDefault,
    }));
}

/**
 * The option that is selected when the URL carries no `?sort=`: live's own
 * `isDefault` row when the list marks one, otherwise the first entry.
 *
 * SCAPI does not expose `isDefault`, but {@link resolveSortOptions} now carries
 * it onto a server list by id match, so the marked row is found on the
 * server-configured path too. The first-entry arm is what remains for a list
 * whose ids the curated list does not recognise at all — and it is a genuine
 * guess, not a reading, so anything relying on it is naming a sort it cannot
 * confirm the results are in.
 */
export function defaultSortId(options: ResolvedSortOption[]): string | undefined {
    return (options.find((o) => o.isDefault) ?? options[0])?.id;
}

/**
 * The option a sort CONTROL should display for a given `?sort=` value.
 *
 * WHY THIS EXISTS RATHER THAN A BARE `find(...) ?? options[0]`. The brands'
 * lists are no longer the same set — {@link BG_SORT_OPTIONS} has no
 * `top-sellers`, because live BG offers no "Best Sellers" row. So a BG URL
 * carrying an NM-only id (a cross-brand link, a preserved query string, a stale
 * bookmark) misses the lookup, and a position-0 fallback then displays
 * `price-high-to-low` — "Price: high to low" over a grid the loader did not
 * sort that way. Reproduced at 1280 on
 * `/BergdorfGoodman/search?q=dress&sort=top-sellers`.
 *
 * Falling back to {@link defaultSortId} instead is the same rule this module's
 * header already states for the no-`?sort=` case, applied to the second way a
 * lookup can come back empty. It is also the truthful label: SCAPI ignores a
 * sorting-option id the site does not define, so the results really are in the
 * default order the control now names.
 *
 * The unrecognized id is deliberately NOT added to the list as a synthetic
 * row — BG's four are a measured set, and inventing a fifth control would put
 * an affordance on the page that live BG does not offer.
 */
export function selectSortOption(
    options: ResolvedSortOption[],
    currentSort: string | undefined
): ResolvedSortOption | undefined {
    const match = options.find((option) => option.id === currentSort);
    if (match) return match;

    const fallbackId = defaultSortId(options);
    return options.find((option) => option.id === fallbackId);
}
